export const UNITS_DE = {
  kg: 'kg',
  km: 'km',
  l: 'L',
  lfm: 'Lfm.',
  m: 'm',
  m2: 'm2',
  m3: 'm3',
  min: 'Min.',
  pkg: 'Pkg.',
  flat_rate: 'psch.',
  std: 'Std.',
  stk: 'Stk.',
  t: 't',
  day: 'Tag(e)',
  qmw: 'qmw',
} as const

const newInvoice = {
  title: 'Rechnung',
  paymentTerms: 'Zahlungsbedingung',
  paymentTermSkontoPreview:
    '{{percent}}% Skonto bei Zahlung innerhalb von {{days}} Tagen (berechnet auf Bruttosumme)',
  bankAccounts: 'Bankkonto',
  selectDate: 'Datum auswählen',
  itemForm: {
    enterSellingPrice: 'Verkaufspreis eingeben',
    exactMarkup: 'Genauer Aufschlag: {{markup}}',
    addItemTitle: 'Freie Position',
    fromCatalog: 'Aus Katalog',
    editItemTitle: 'Position bearbeiten',
    basicInfo: 'Grundlegende Informationen',
    basicInfoDescription: 'Gib die grundlegenden Informationen für die Position ein.',
    mark: 'Positionskennzeichen',
    markDescription:
      'Gekennzeichnete Positionen bleiben sichtbar, werden aber nicht in die Summe eingerechnet.',
    markNone: 'Kein Kennzeichen',
    markAlternative: 'Alternativ',
    markContingency: 'Eventual',
    imageLabel: 'Bild',
    imageDescription: 'Füge ein optionales Bild für diese Position hinzu.',
    imageSupports: 'PNG, JPG, GIF oder SVG bis zu 10 MB',
    imageHint: 'Das Bild wird auf der Rechnungsposition gespeichert und im PDF angezeigt.',
    imagePreviewAlt: 'Vorschau des Positionsbilds',
    imageUpload: 'Bild hochladen',
    imageChange: 'Bild ändern',
    imageUploading: 'Bild wird hochgeladen',
    measurements: 'Aufmaß',
    measurementsDescription:
      'Nur intern, wird nicht gedruckt. Die Ergebnisse ergeben die Menge, die gesperrt bleibt, solange das Aufmaß aktiv ist.',
    measurementsColumnDescription: 'Beschreibung',
    measurementsColumnFormula: 'Formel',
    measurementsColumnResult: 'Ergebnis',
    measurementsAddRow: 'Zeile hinzufügen',
    measurementsDeleteRow: 'Zeile löschen',
    measurementsInvalidFormula: 'Nur Zahlen, + - * / und Klammern',
    measurementsOutOfRange: 'Das Aufmaß muss zwischen 0,01 und 999.999,99 ergeben',
    measurementsDiscardConfirm: 'Aufmaß entfernen? Seine Zeilen werden gelöscht.',
    measurementsKeep: 'Behalten',
    measurementsRemove: 'Entfernen',
    measurementsTotal: 'Summe',
    priceConfiguration: 'Preiskonfiguration',
    priceConfigurationDescription:
      'Konfigurieren Sie die Artikelpreise. Der Verkaufspreis und der Aufschlagsprozentsatz sind miteinander verknüpft und werden während der Eingabe automatisch aktualisiert.',
    discount: 'Rabatt',
    discountDescription: 'Füge dem Gesamtbetrag einen Rabatt hinzu.',
  },
  groupItemTitle: 'Positionen gruppieren',
  groupItemEditTitle: 'Gruppe bearbeiten',
  groupItemDescription: 'Erstelle eine Titellinie, um die ausgewählten Positionen zu gruppieren.',
  expiryDate: 'Ablaufdatum',
  deliveryDate: 'Lieferdatum',
  fulfillmentDate: 'Leistungsdatum',
  subtotalNet: 'Gesamtbetrag Netto',
  itemsTotal: 'Positionen',
  addTopMemo: '+ Einleitungstext hinzufügen',
  addBottomMemo: '+ Schlusstext hinzufügen',
  total: 'Gesamtbetrag Brutto',
  discountValue: 'Rabattwert',
  impact: 'Einfluss',
  vat: 'USt',
  lineItems: 'Positionen',
  description: 'Beschreibung',
  quantity: 'Menge',
  price: 'Preis',
  introductionNote: 'Einleitungstext',
  closingNote: 'Schlusstext',
  deliveryTimeframe: 'Liefertermin / Ausführungszeitraum',
  orderConfirmationTerms: 'Konditionen',
  orderConfirmationTexts: 'Texte',
  orderConfirmationItemsLocked:
    'Die Positionen stammen aus dem Angebot und können hier nicht geändert werden. Nutze ein Nachtragsangebot, um den Leistungsumfang zu ändern.',
  vatLong: 'Umsatzsteuer',
  negativeTotalWarning: 'Die Summe dieses Dokuments ist negativ.',
  taxExemption: {
    label: 'Steuerbefreiung',
    placeholder: 'Grund auswählen',
    description:
      'Wird als gesetzlicher Hinweis auf dem Dokument gedruckt und in die E-Rechnung übernommen.',
    noteLabel: 'Begründung',
    notePlaceholder: 'z. B. Steuerfrei nach § 4 Nr. 21 UStG',
    error: {
      reasonRequired:
        'Dieses Dokument enthält eine Position mit 0 % Umsatzsteuer. Öffne es und gib an, warum der Betrag steuerfrei ist, bevor du es ausstellst.',
    },
    reason: {
      small_business: 'Kleinunternehmer (§ 19 UStG)',
      reverse_charge: 'Reverse Charge / Bauleistung (§ 13b UStG)',
      intra_community: 'Innergemeinschaftliche Lieferung (§ 4 Nr. 1b UStG)',
      export: 'Ausfuhrlieferung ins Drittland (§ 4 Nr. 1a UStG)',
      exempt_other: 'Andere Steuerbefreiung (§ 4 UStG)',
    },
  },
  item: {
    name: 'Name',
    qty: 'Menge',
    price: 'Preis',
    amount: 'Betrag',
    description: 'Beschreibung',
    placeholderName: 'Positionsname',
  },
  signatureToggle: 'Unterschriftsbereich anzeigen',
  signatureToggleDescription: 'Fügt am Ende des Dokuments Linien für Datum, Ort und Unterschrift hinzu.',
  showPricesToggle: 'Preise erfassen',
  showPricesToggleDescription:
    'Preise der Positionen festhalten, für die Rechnung. Sie erscheinen nicht auf dem Lieferschein.',
  automatedReminders: {
    label: 'Automatische Erinnerungen',
    description: 'Steuert, ob fuer diese Rechnung automatische Erinnerungen versendet werden.',
  },
  laborHours: {
    switchLabel: 'Arbeitsstunden auf dem Dokument',
    switchDescription:
      'Metadaten zu Arbeitsstunden fuer diesen Entwurf speichern. Sie koennen im PDF erscheinen, wenn Ihr Unternehmen Arbeitsstunden nutzt.',
    hoursLabel: 'Gearbeitete Stunden',
    hoursPlaceholder: 'z. B. 8,50',
    costLabel: 'Kosten der Arbeitsstunden',
    costPlaceholder: 'z. B. 1.250,00',
    customTextLabel: 'Eigener Text (optional)',
    customTextPlaceholder: 'Zusaetzliche Angaben zu den Arbeitsstunden',
    invalidHours: 'Bitte null oder eine positive Zahl eingeben (Dezimaltrennzeichen: Komma).',
    invalidCost: 'Bitte einen Betrag groesser null eingeben (Dezimaltrennzeichen: Komma).',
    hintTitle: 'Vorschlag aus Positionen (Std., Tag)',
    hintTotalLabel: 'Summe brutto (Std. + Tag)',
    hintEmpty: 'Noch keine Positionen mit Einheit Std. oder Tag auf diesem Dokument.',
    previewHoursLabel: 'Arbeitsstunden',
    previewCostLabel: 'Kosten der Arbeitsstunden',
    previewEmptyHours: '0,00 Std.',
  },
  sepaQrPreview: {
    qrCodeAlt: 'EPC-QR-Code (Girocode) über {{amount}}',
  },
  signatureArea: 'Unterschriftsbereich',
  signaturePlaceholder: 'Unterschrift wird hier angezeigt',
  dateLocation: 'Datum, Ort',
  signature: 'Unterschrift',
  job: {
    label: 'Auftrag',
    selectPlaceholder: 'Auftrag und Kunde auswählen',
    searchPlaceholder: 'Auftrag suchen',
    searching: 'Suche nach Aufträgen...',
    recent: 'Zuletzt erstellt',
    noResults: 'Keine Aufträge gefunden.',
  },
  units: UNITS_DE,
  clientRequired: {
    title: 'Auftrag ohne Kunde',
    description: 'Bitte verknüpfe den Auftrag mit einem Kunden um Rechnungsdokumente zu erstellen.',
    ok: 'OK',
  },
  clientNameRequired: {
    title: 'Fehlender Vorname',
    description: 'Bitte gib den Vorname deines Kunden ein um fortzufahren.',
    firstName: 'Vorname',
  },
  discount: {
    add: 'Rabatt',
    edit: 'Rabatt bearbeiten',
    title: 'Rabatt hinzufügen',
    addDescription: 'Füge dem Gesamtbetrag einen Rabatt hinzu.',
    editDescription: 'Bearbeite den Rabatt für den Gesamtbetrag.',
    type: 'Rabatttyp',
    amount: 'Festbetrag',
    percentage: 'Prozent',
    apply: 'Rabatt anwenden',
    cancel: 'Abbrechen',
    remove: 'Rabatt entfernen',
    label: 'Rabatt',
    invalidValue: 'Ungültiger Rabattwert',
  },
  billableHours: {
    title: 'Fakturierbare Stunden',
    description: 'Fakturierbare Stunden von diesem Auftrag zur Rechnung hinzufügen',
    noJobSelected: 'Kein Job ausgewählt',
    loading: 'Zeiten werden geladen...',
    noBillableHours: 'Keine fakturierbaren Stunden gefunden.',
    selectHours: 'Zeiten auswählen',
    selectAll: 'Alle auswählen',
    generatedLineItems: 'Generierte Positionen',
    customItem: 'Benutzerdefiniert',
    catalogItem: 'Katalog',
    total: 'Gesamt',
    entriesCount_zero: 'Einträge',
    entriesCount_one: 'Eintrag',
    entriesCount_other: 'Einträge',
    addToInvoice: 'Zur Rechnung hinzufügen',
    selected: 'Ausgewählt',
    usedBillableHours: 'Auf Rechnungen verwendete Einträge von diesem Auftrag',
    groupsSelected: 'Gruppen ausgewählt',
    groupCategories: 'Kategorien zusammenfassen',
    onCurrentInvoice: 'Aktueller Rechnung',
    noUnbilledEntries: 'Keine unverbuchten Einträge für diesen Auftrag',
  },
  materials: {
    title: 'Materialien',
    description: 'Materialien aus diesem Auftrag zur Rechnung hinzufügen',
    noJobSelected: 'Kein Job ausgewählt',
    loading: 'Materialien werden geladen...',
    noMaterials: 'Keine Materialien gefunden.',
    selectMaterials: 'Materialien auswählen',
    selectAll: 'Alle auswählen',
    addToInvoice: 'Zur Rechnung hinzufügen',
    noUnusedMaterials: 'Keine unbenutzten Materialien gefunden.',
    usedOnReceivable: 'Verwendet auf Rechnung {{receivableId}}',
    usedMaterials: 'Verwendete Materialien',
    onThisDraft: 'Aktueller Rechnung',
    used: 'Verwendet',
  },
  bundle: {
    create: 'Baustein erstellen',
    addToBundle: 'Zum Baustein hinzufügen',
    receivableBundle: 'Rechnungsbaustein',
    createDescription: 'Baustein erstellt aus ausgewählten Positionen',
    selectAtLeastTwoItems: 'Wähle mindestens zwei Positionen aus, um einen Baustein zu erstellen',
    cannotBundleExistingBundles:
      'Positionen, die bereits Teil eines Bausteins sind, können nicht gebündelt werden',
    cannotBundleTextPosition: 'Textpositionen können nicht in einen Baustein aufgenommen werden.',
    cannotBundleDifferentVatRates:
      'Positionen mit unterschiedlichen USt-Sätzen können nicht gebündelt werden. Alle Positionen müssen den gleichen USt-Satz haben.',
    cannotCombineMultipleBundles:
      'Positionen können nicht kombiniert werden, wenn mehr als ein bestehender Baustein ausgewählt ist',
    cannotCombineBundleType: 'Zu diesem Bausteintyp können keine Positionen hinzugefügt werden',
    createBundleTooltip: 'Erstelle einen Baustein aus den ausgewählten Positionen',
    addToBundleTooltip: 'Füge die ausgewählten Positionen zum bestehenden Baustein hinzu',
    items: 'Positionen',
  },
  markupDialog: {
    title: 'Aufschlag anwenden',
    description: 'Anpassen aller Positionen um einen Prozentsatz und Vorschau der Gesamtsumme.',
  },
  importGaeb: 'GAEB importieren',
  groupCreate: 'Gruppe',
  toolbar: {
    fromJob: 'Aus Auftrag:',
  },
  selectionBar: {
    label: 'Aktionen für die Auswahl',
    count: '{{count}} ausgewählt',
    clear: 'Auswahl aufheben',
    moveTo: 'Verschieben nach…',
    moveToLabel: 'Auf Position verschieben',
    moveToSubmit: 'Verschieben',
    moveToErrors: {
      invalidNumber:
        'Bitte eine Positionsnummer eingeben, z. B. 3 oder 2.1 innerhalb einer Gruppe.',
      outOfRange: 'Bitte eine Position von {{first}} bis {{last}} wählen.',
      outsideGroup:
        'Positionen in einer Gruppe lassen sich nur innerhalb ihrer Gruppe verschieben ({{groupLabel}}.1, {{groupLabel}}.2, …).',
      intoGroup:
        'Positionen lassen sich nicht in eine Gruppe verschieben. Bitte eine Nummer ohne Punkt eingeben.',
      mixedSelection:
        'Zum gemeinsamen Verschieben nur Positionen aus einer Gruppe oder nur Positionen ohne Gruppe auswählen.',
    },
  },
  textPosition: {
    button: 'Text',
    addTitle: 'Textposition hinzufügen',
    editTitle: 'Textposition bearbeiten',
    description:
      'Ein reiner Text ohne Menge und Preis, z. B. ein Raumname oder ein Hinweis. Er geht in keine Summe ein.',
    label: 'Text',
    cannotGroupOnly:
      'Eine Gruppe braucht mindestens eine Position mit Betrag. Textpositionen allein genügen nicht.',
    empty: 'Leere Textposition',
  },
  deleteSelected: 'Löschen',
  deleteSelectedDialog: {
    title_one: '{{count}} Position löschen?',
    title_other: '{{count}} Positionen löschen?',
    description_one:
      'Die ausgewählte Position wird aus dem Dokument entfernt. Gespeichert wird erst, wenn du das Dokument speicherst.',
    description_other:
      'Die {{count}} ausgewählten Positionen werden aus dem Dokument entfernt. Gespeichert wird erst, wenn du das Dokument speicherst.',
    bundleWarning_one:
      'Die Auswahl enthält einen Baustein, dadurch wird auch {{count}} enthaltene Position gelöscht.',
    bundleWarning_other:
      'Die Auswahl enthält einen Baustein, dadurch werden auch {{count}} enthaltene Positionen gelöscht.',
    confirm: 'Löschen',
    cancel: 'Abbrechen',
  },
  errors: {
    invalidGaebImport: 'GAEB import fehlgeschlagen.',
  },
} as const

export default newInvoice
